import type * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { sheetStructureRef } from "./panel-structure";
import { DialogBackdrop, DialogBody, DialogFooter, DialogForm, DialogPortal } from "./dialog";

/**
 * ADR-0087: a right-hand panel for one selected item. Its fixed header holds the title, optional
 * subtext, and the close button; children are a SheetBody and an optional SheetFooter (or both
 * inside a SheetForm). Change a sheet's content in place instead of opening a dialog on top.
 */
function Sheet({
  open,
  onClose,
  title,
  kicker,
  description,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  /** Without a title, the children render their own SheetHeader (for content that changes in place). */
  title?: React.ReactNode;
  kicker?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogPrimitive.Popup
          ref={sheetStructureRef}
          data-slot="sheet"
          className={cn(
            "fixed inset-y-0 right-0 z-50 flex w-[min(520px,100vw)] flex-col bg-card text-card-foreground shadow-elevated outline-none duration-200 data-open:animate-in data-open:slide-in-from-right-8 data-open:fade-in-0 data-closed:animate-out data-closed:slide-out-to-right-8 data-closed:fade-out-0 motion-reduce:animate-none",
            className,
          )}
        >
          {title === undefined ? null : (
            <SheetHeader title={title} kicker={kicker} description={description} />
          )}
          {children}
        </DialogPrimitive.Popup>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}

/** The fixed top of a sheet: optional kicker, the title, optional subtext, and the close button. */
function SheetHeader({
  title,
  kicker,
  description,
}: {
  title: React.ReactNode;
  kicker?: React.ReactNode;
  description?: React.ReactNode;
}) {
  return (
    <div
      className="flex shrink-0 items-start gap-3 px-6 pt-6 pb-4 sm:px-8 sm:pt-7"
      data-slot="dialog-header"
    >
      <div className="min-w-0 flex-1">
        {kicker ? (
          <p className="m-0 text-xs font-semibold text-muted-foreground">{kicker}</p>
        ) : null}
        <DialogPrimitive.Title className="m-0 font-heading text-2xl leading-tight font-extrabold text-balance">
          {title}
        </DialogPrimitive.Title>
        {description ? (
          <DialogPrimitive.Description className="m-0 mt-1 text-sm text-muted-foreground">
            {description}
          </DialogPrimitive.Description>
        ) : null}
      </div>
      <DialogPrimitive.Close
        aria-label="Close"
        className="grid size-11 shrink-0 place-items-center rounded-md bg-muted text-foreground outline-none hover:bg-[color-mix(in_srgb,var(--surface-2)_80%,var(--line))] focus-visible:shadow-[var(--focus-ring)]"
      >
        <XIcon aria-hidden="true" className="size-5" />
      </DialogPrimitive.Close>
    </div>
  );
}

function SheetBody({ className, ...props }: React.ComponentProps<typeof DialogBody>) {
  return <DialogBody className={cn("space-y-6 sm:px-8", className)} {...props} />;
}

function SheetFooter({ className, ...props }: React.ComponentProps<typeof DialogFooter>) {
  return <DialogFooter className={cn("sm:px-8", className)} {...props} />;
}

const SheetForm = DialogForm;

/** One label-over-value pair inside a sheet. */
function SheetField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export { Sheet, SheetBody, SheetField, SheetFooter, SheetForm, SheetHeader };
